import { Tab } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  PageHeader,
  PostSavePromptProvider,
  QueryTabPanel,
  QueryTabs,
  UnsavedChangesGuardProvider,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import {
  ManageShop,
  ManageShopTab,
  TabbedManageShop,
  ShopIntegrations,
} from 'features/shop';
import { useAppHistory, useMeta, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

enum ShopDetailsTab {
  Shop = 'shop',
  Integrations = 'integrations',
}

// Flat tabs used when isShopTabsEnabled — shop section tabs + integrations at top level
enum TabbedShopDetailsTab {
  General = 'general',
  Sci = 'sci',
  Payin = 'payin',
  Payout = 'payout',
  Cascade = 'cascade',
  Integrations = 'integrations',
}

// Map TabbedShopDetailsTab → ManageShopTab (for shop section tabs)
const SHOP_SECTION_TABS: Partial<Record<TabbedShopDetailsTab, ManageShopTab>> =
  {
    [TabbedShopDetailsTab.General]: ManageShopTab.General,
    [TabbedShopDetailsTab.Sci]: ManageShopTab.Sci,
    [TabbedShopDetailsTab.Payin]: ManageShopTab.Payin,
    [TabbedShopDetailsTab.Payout]: ManageShopTab.Payout,
    [TabbedShopDetailsTab.Cascade]: ManageShopTab.Cascade,
  };

export const ShopDetailsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });

  const {
    isAdmin,
    isTechOperator,
    isMerchant,
    isMerchantOperator,
    isAdminOrTechOperator,
  } = useUser();
  const { isShopTabsEnabled } = useMeta();

  const { goBack } = useAppHistory();

  const { id: shopId = '' } = useParams<{ id: string }>();
  const isNew = useMemo(() => shopId === NEW_ID, [shopId]);
  const title = useMemo(
    () => t(isNew ? 'new_shop.title' : 'edit_shop.title'),
    [isNew, t],
  );

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOPS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOPS;
    } else if (isMerchant || isMerchantOperator) {
      return ROUTE_PATH.MERCHANT.SHOPS;
    }
  }, [isAdmin, isTechOperator, isMerchant, isMerchantOperator]);

  const navigateBack = useCallback(() => {
    goBack(backUrl!);
  }, [backUrl, goBack]);

  const tabProps = useMemo(
    () => ({ shopId, navigateBack }),
    [shopId, navigateBack],
  );

  // Flat tabbed mode: shop section tabs + integrations at top level
  if (isShopTabsEnabled) {
    return (
      <UnsavedChangesGuardProvider>
        <PostSavePromptProvider navigateBack={navigateBack}>
          <div>
            <PageHeader title={title} divider={false} />
            <QueryTabs tabsEnum={TabbedShopDetailsTab}>
              <Tab
                value={TabbedShopDetailsTab.General}
                label={t('tabs.general')}
              />
              <Tab
                value={TabbedShopDetailsTab.Sci}
                label={t('tabs.sci')}
                disabled={isNew}
              />
              <Tab
                value={TabbedShopDetailsTab.Payin}
                label={t('tabs.payin')}
                disabled={isNew}
              />
              <Tab
                value={TabbedShopDetailsTab.Payout}
                label={t('tabs.payout')}
                disabled={isNew}
              />
              {isAdminOrTechOperator && (
                <Tab
                  value={TabbedShopDetailsTab.Cascade}
                  label={t('tabs.cascade')}
                  disabled={isNew}
                />
              )}
              {isAdminOrTechOperator && (
                <Tab
                  value={TabbedShopDetailsTab.Integrations}
                  label={t('edit_shop.tabs.integrations')}
                  disabled={isNew}
                />
              )}
            </QueryTabs>

            {Object.entries(SHOP_SECTION_TABS).map(([tabValue, shopTab]) => (
              <QueryTabPanel key={tabValue} value={tabValue}>
                <TabbedManageShop {...tabProps} currentTab={shopTab} />
              </QueryTabPanel>
            ))}

            {isAdminOrTechOperator && !isNew && (
              <QueryTabPanel value={TabbedShopDetailsTab.Integrations}>
                <ShopIntegrations {...tabProps} />
              </QueryTabPanel>
            )}
          </div>
        </PostSavePromptProvider>
      </UnsavedChangesGuardProvider>
    );
  }

  // Legacy mode (isShopTabsEnabled=false) or new shop creation
  const hideTabs = isNew || !isAdminOrTechOperator;

  if (hideTabs) {
    return (
      <div>
        <PageHeader title={title} />
        <ManageShop {...tabProps} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={title} divider={false} />
      <QueryTabs tabsEnum={ShopDetailsTab}>
        <Tab value={ShopDetailsTab.Shop} label={t('edit_shop.tabs.shop')} />
        {isAdminOrTechOperator && (
          <Tab
            value={ShopDetailsTab.Integrations}
            label={t('edit_shop.tabs.integrations')}
          />
        )}
      </QueryTabs>
      <QueryTabPanel value={ShopDetailsTab.Shop}>
        <ManageShop {...tabProps} />
      </QueryTabPanel>
      {isAdminOrTechOperator && (
        <QueryTabPanel value={ShopDetailsTab.Integrations}>
          <ShopIntegrations {...tabProps} />
        </QueryTabPanel>
      )}
    </div>
  );
};
